import { useCallback, useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { Button, LinearProgress, Tooltip } from "@mui/material";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RemoveIcon from "@mui/icons-material/Remove";
import axios from "axios";
import STOCK_URL from "../../utils/STOCK_URL.js";

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const DrawerTitle = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const DrawerContent = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));

const StyledTable = styled("table")(({ theme }) => ({
  width: "100%",
  borderCollapse: "collapse",
  "tbody tr td": {
    padding: "0.4rem 0.6rem",
    fontSize: "0.9rem",
  },
  "tbody tr td:first-of-type": {
    color: "#ffffff",
    width: "50%",
  },
  "tbody tr td:last-of-type": {
    color: "#878a99",
    textAlign: "right",
  },
}));

const ProgressBarContainer = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  marginBottom: "0.8rem",
  "& .progress-label": {
    color: "#ffffff",
    fontSize: "0.9rem",
    minWidth: "80px",
  },
  "& .progress-bar": {
    flex: 1,
    height: "8px",
    borderRadius: "4px",
    backgroundColor: "#333",
  },
  "& .progress-value": {
    color: "#878a99",
    fontSize: "0.9rem",
    minWidth: "50px",
    textAlign: "right",
  },
}));

const StyledLinearProgress = styled(LinearProgress)(({ theme, color }) => ({
  height: 8,
  borderRadius: 4,
  backgroundColor: "#333",
  "& .MuiLinearProgress-bar": {
    backgroundColor:
      color === "red"
        ? "#ff4757"
        : color === "orange"
        ? "#ffa726"
        : color === "blue"
        ? "#42a5f5"
        : "#66bb6a",
    borderRadius: 4,
  },
}));

function StockSideBar({
  stockOpen,
  setStockOpen,
  currentMchId,
  setMonthStockGraphOpen,
  setDayStockGraphOpen,
}) {
  const { group_code, project_code } = useParams();
  const [stockData, setStockData] = useState({
    daily_in_quantity: "0",
    daily_out_quantity: "0",
    end_stock_quantity: "0",
    excess_stock_quantity: "0",
    excess_stock_ratio: "0",
    liquid_stock_quantity: "0",
    liquid_stock_ratio: "0",
    longterm_stock_quantity: "0",
    location_code: "",
    obsolete_stock_quantity: "0",
    obsolete_stock_ratio: "0",
    previous_stock_quantity: "0",
    total_current_stock: "0",
    longterm_stock_ratio: "0",
    prev_month_avg_stock_quantity: "0",
    current_month_out_quantity: "0",
  });
  const fetchData = async () => {
    try {
      const res = await axios.get(
        STOCK_URL +
          `/api/stock/basic?group_code=${group_code}&project_code=${project_code}&currentMchId=${currentMchId}`
      );
      const dataObj = res.data;
      setStockData(dataObj);
    } catch (e) {
      console.log(e);
    }
  };
  useEffect(() => {
    if (stockOpen) {
      fetchData();
    }
  }, [stockOpen, currentMchId]);
  return (
    <Drawer
      anchor="right"
      open={stockOpen}
      onClose={() => setStockOpen(false)}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        "& .MuiDrawer-paper": {
          backgroundColor: "#2d2d2d",
          color: "#ffffff",
          width: 350,
          height: "100vh",
          overflow: "hidden",
        },
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      <Box
        sx={{
          paddingY: "0.5rem",
          paddingX: "0.75rem",
          backgroundColor: "#1a1a1a",
        }}
      >
        <DrawerHeader>
          <Box sx={{ fontSize: "1.5em", fontWeight: 500 }}>
            {stockData.location_code?.slice(0, 4)}
          </Box>
          <IconButton
            onClick={() => setStockOpen(false)}
            sx={{ color: "#ced4da" }}
          >
            <CloseIcon />
          </IconButton>
        </DrawerHeader>
      </Box>
      <Divider sx={{ backgroundColor: "#444" }} />
      <Box sx={{ padding: "1rem" }}>
        <Box
          sx={{ color: "#ffffff", fontSize: "1rem", marginBottom: "0.5rem" }}
        >
          일간 재고 일수
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Tooltip title="현재 재고 ÷ 평균출고량">
            <Box
              sx={{
                flex: 1,
                height: "20px",
                background: `linear-gradient(90deg, #4285f4 0%, #4285f4 ${
                  ((isNaN(Number(stockData.today_stock_day?.replace("일", "")))
                    ? 0
                    : Number(stockData.today_stock_day?.replace("일", ""))) /
                    60) *
                  100
                }%, #333 ${
                  ((isNaN(Number(stockData.today_stock_day?.replace("일", "")))
                    ? 0
                    : Number(stockData.today_stock_day?.replace("일", ""))) /
                    60) *
                  100
                }%)`,
                borderRadius: "4px",
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  left: `${Math.min(
                    ((isNaN(
                      Number(stockData.today_stock_day?.replace("일", ""))
                    )
                      ? 0
                      : Number(stockData.today_stock_day?.replace("일", ""))) /
                      60) *
                      100,
                    100
                  )}%`,
                  top: "-2px",
                  bottom: "-2px",
                  width: "2px",
                  backgroundColor: "#fff",
                },
              }}
            />
          </Tooltip>{" "}
          <Box
            sx={{ color: "#ffffff", fontSize: "1.2rem", fontWeight: "bold" }}
          >
            {stockData.today_stock_day}
          </Box>
        </Box>
      </Box>
      <Divider sx={{ backgroundColor: "#444" }} />
      <Box sx={{ backgroundColor: "#1a1a1a", padding: "1rem" }}>
        <Box sx={{ fontSize: "1.2em", fontWeight: 500, color: "#ffffff" }}>
          재고정보
        </Box>
      </Box>
      <Divider sx={{ backgroundColor: "#444" }} />
      <Box sx={{ padding: "1rem" }}>
        <StyledTable>
          <tbody>
            <tr>
              <td>현재 재고량</td>
              <td>
                {Number(stockData.previous_stock_quantity) +
                  Number(stockData.daily_in_quantity) -
                  Number(stockData.daily_out_quantity)}{" "}
                EA
              </td>
            </tr>
            <tr>
              <td>전일 재고량</td>
              <td>{stockData.previous_stock_quantity} EA</td>
            </tr>
            <tr>
              <td>당일 입고량</td>
              <td>{stockData.daily_in_quantity} EA</td>
            </tr>
            <tr>
              <td>당일 출고량</td>
              <td>{stockData.daily_out_quantity} EA</td>
            </tr>
            <tr>
              <td>당일 증감량</td>
              <td
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.3rem",
                  justifyContent: "flex-end",
                }}
              >
                {(() => {
                  const difference =
                    stockData.daily_in_quantity - stockData.daily_out_quantity;
                  if (difference > 0) {
                    return (
                      <ArrowUpwardIcon
                        sx={{ fontSize: "1rem", color: "#4caf50" }}
                      />
                    );
                  } else if (difference < 0) {
                    return (
                      <ArrowDownwardIcon
                        sx={{ fontSize: "1rem", color: "#f44336" }}
                      />
                    );
                  } else {
                    return (
                      <RemoveIcon sx={{ fontSize: "1rem", color: "#757575" }} />
                    );
                  }
                })()}
                {Math.abs(
                  stockData.daily_in_quantity - stockData.daily_out_quantity
                )}
                EA
              </td>
            </tr>
          </tbody>
        </StyledTable>
      </Box>
      <Divider sx={{ backgroundColor: "#444" }} />
      <Box sx={{ backgroundColor: "#1a1a1a", padding: "1rem" }}>
        <Box sx={{ fontSize: "1.2em", fontWeight: 500, color: "#ffffff" }}>
          <DrawerContent>
            <ProgressBarContainer>
              <div className="progress-label">월 재고일수</div>
              <Tooltip title="(당월 평균재고량 ÷ 당월 출고량) × 당월 일수">
                <div className="progress-bar">
                  <StyledLinearProgress
                    variant="determinate"
                    value={75}
                    color="red"
                  />
                </div>
              </Tooltip>
              <div className="progress-value">
                {stockData.month_stock_day}일
              </div>
            </ProgressBarContainer>
            <ProgressBarContainer>
              <div className="progress-label">유동재고율</div>
              <div className="progress-bar">
                <StyledLinearProgress
                  variant="determinate"
                  value={Number(stockData.liquid_stock_ratio)}
                  color="blue"
                />
              </div>
              <div className="progress-value">
                {stockData.liquid_stock_ratio}%
              </div>
            </ProgressBarContainer>
            <ProgressBarContainer>
              <div className="progress-label">과잉재고율</div>
              <div className="progress-bar">
                <StyledLinearProgress
                  variant="determinate"
                  value={Number(stockData.excess_stock_ratio)}
                  color="orange"
                />
              </div>
              <div className="progress-value">
                {stockData.excess_stock_ratio}%
              </div>
            </ProgressBarContainer>
            <ProgressBarContainer>
              <div className="progress-label">장기재고율</div>
              <div className="progress-bar">
                <StyledLinearProgress
                  variant="determinate"
                  value={Number(stockData.longterm_stock_ratio)}
                  color="red"
                />
              </div>
              <div className="progress-value">
                {stockData.longterm_stock_ratio}%
              </div>
            </ProgressBarContainer>
            <ProgressBarContainer>
              <div className="progress-label">불용재고율</div>
              <div className="progress-bar">
                <StyledLinearProgress
                  variant="determinate"
                  value={Number(stockData.obsolete_stock_ratio)}
                  color="red"
                />
              </div>
              <div className="progress-value">
                {stockData.obsolete_stock_ratio}%
              </div>
            </ProgressBarContainer>
          </DrawerContent>
        </Box>
      </Box>
      <Divider sx={{ backgroundColor: "#444" }} />
      <Box sx={{ padding: "1rem" }}>
        <StyledTable>
          <tbody>
            <tr>
              <td>전월 평균재고액</td>
              <td>120,000,000 원</td>
            </tr>
            <tr>
              <td>전월 평균재고량</td>
              <td>
                {Math.floor(Number(stockData.prev_month_avg_stock_quantity))} EA
              </td>
            </tr>
            <tr>
              <td>당월 출고액</td>
              <td>42,000,000 원</td>
            </tr>
            <tr>
              <td>당월 출고량</td>
              <td>{stockData.current_month_out_quantity} EA</td>
            </tr>
          </tbody>
        </StyledTable>
      </Box>
      <Divider sx={{ backgroundColor: "#444" }} />
      <Box sx={{ backgroundColor: "#1a1a1a", padding: "1rem" }}>
        <Box sx={{ fontSize: "1.2em", fontWeight: 500, color: "#ffffff" }}>
          유동수 정보
        </Box>
      </Box>
      <Divider sx={{ backgroundColor: "#444" }} />
      <Box
        sx={{
          padding: "1rem",
          display: "flex",
          gap: "1rem",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Button
          variant="outlined"
          size="small"
          sx={{
            color: "#ffffff",
            borderColor: "#555",
            "&:hover": {
              borderColor: "#777",
              backgroundColor: "#333",
            },
          }}
          onClick={() => setMonthStockGraphOpen(true)}
        >
          월간 재고일수 추이
        </Button>
        <Button
          variant="outlined"
          size="small"
          sx={{
            color: "#ffffff",
            borderColor: "#555",
            "&:hover": {
              borderColor: "#777",
              backgroundColor: "#333",
            },
          }}
          onClick={() => setDayStockGraphOpen(true)}
        >
          일간 재고 추이
        </Button>
      </Box>
    </Drawer>
  );
}

export default StockSideBar;
